/**
 * Export menu component
 * Export options dropdown
 */

import { useState } from 'react';
import { Button, Dropdown, Modal, Form, Select, Checkbox, Space, Typography, Radio, Alert, Progress } from 'antd';
import type { MenuProps } from 'antd';
import {
  DownloadOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import type { ExportFormat } from '@/types/place';

const { Text } = Typography;

interface ExportMenuProps {
  onExport: (format: ExportFormat, options?: ExportOptions) => void;
  loading?: boolean;
  disabled?: boolean;
  totalResults?: number;
  selectedCount?: number;
}

interface ExportOptions {
  format: ExportFormat;
  columns?: string[];
  includeHeaders?: boolean;
  exportSelected?: boolean;
}

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'rating', label: 'Rating' },
  { key: 'reviewCount', label: 'Review Count' },
  { key: 'address', label: 'Address' },
  { key: 'phone', label: 'Phone' },
  { key: 'website', label: 'Website' },
  { key: 'latitude', label: 'Latitude' },
  { key: 'longitude', label: 'Longitude' },
  { key: 'priceLevel', label: 'Price Level' },
  { key: 'openingHours', label: 'Opening Hours' },
  { key: 'placeId', label: 'Place ID' },
  { key: 'url', label: 'Google Maps URL' },
];

export function ExportMenu({
  onExport,
  loading,
  disabled,
  totalResults = 0,
  selectedCount = 0,
}: ExportMenuProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const handleQuickExport = (format: ExportFormat) => {
    onExport(format);
  };

  const handleAdvancedExport = () => {
    form.validateFields().then((values) => {
      onExport(values.format, values);
      setModalOpen(false);
    });
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'csv',
      icon: <FileTextOutlined />,
      label: 'Export as CSV',
      onClick: () => handleQuickExport('csv'),
    },
    {
      key: 'xlsx',
      icon: <FileExcelOutlined />,
      label: 'Export as Excel',
      onClick: () => handleQuickExport('xlsx'),
    },
    {
      key: 'json',
      icon: <CodeOutlined />,
      label: 'Export as JSON',
      onClick: () => handleQuickExport('json'),
    },
    { type: 'divider' },
    {
      key: 'advanced',
      label: 'Advanced Export...',
      onClick: () => setModalOpen(true),
    },
  ];

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        disabled={disabled || loading}
        trigger={['click']}
      >
        <Button icon={<DownloadOutlined />} loading={loading}>
          Export
        </Button>
      </Dropdown>

      {/* Advanced export modal */}
      <Modal
        title="Export Results"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleAdvancedExport}
        okText="Export"
        okButtonProps={{ icon: <DownloadOutlined />, loading }}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            format: 'xlsx',
            columns: EXPORT_COLUMNS.slice(0, 10).map((c) => c.key),
            includeHeaders: true,
            exportSelected: false,
          }}
        >
          {/* Format selection */}
          <Form.Item name="format" label="Export Format">
            <Radio.Group>
              <Radio.Button value="csv">
                <Space>
                  <FileTextOutlined />
                  CSV
                </Space>
              </Radio.Button>
              <Radio.Button value="xlsx">
                <Space>
                  <FileExcelOutlined />
                  Excel
                </Space>
              </Radio.Button>
              <Radio.Button value="json">
                <Space>
                  <CodeOutlined />
                  JSON
                </Space>
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* What to export */}
          {selectedCount > 0 && (
            <Form.Item name="exportSelected" valuePropName="checked">
              <Checkbox>
                Export only selected ({selectedCount} places)
              </Checkbox>
            </Form.Item>
          )}

          {/* Column selection */}
          <Form.Item name="columns" label="Columns to Export">
            <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {EXPORT_COLUMNS.map((col) => (
                <Checkbox key={col.key} value={col.key}>
                  {col.label}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>

          {/* Options */}
          <Form.Item name="includeHeaders" valuePropName="checked">
            <Checkbox>Include column headers</Checkbox>
          </Form.Item>

          {/* Export info */}
          <Alert
            type="info"
            message={
              <Space direction="vertical" size={4}>
                <Text>
                  {selectedCount > 0 && form.getFieldValue('exportSelected')
                    ? `${selectedCount} places will be exported`
                    : `${totalResults} places will be exported`}
                </Text>
              </Space>
            }
          />
        </Form>
      </Modal>
    </>
  );
}

/**
 * Export progress indicator
 */
export function ExportProgress({
  progress,
  format,
  onCancel,
}: {
  progress: number;
  format: ExportFormat;
  onCancel?: () => void;
}) {
  return (
    <div
      style={{
        padding: 16,
        background: 'var(--color-bg-spotlight)',
        borderRadius: 8,
      }}
    >
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Text>Exporting to {format.toUpperCase()}...</Text>
        <Progress percent={progress} status="active" />
        {onCancel && (
          <Button size="small" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Space>
    </div>
  );
}

export default ExportMenu;
